/**
 * @file functions/setOrderAddress.ts
 * 
 * Utility function to set the shipping and billing address for an order.
 * 
 * @param {OrderState} orderState - The current state of the order.
 * @param {string} email - The email address associated with the order.
 * @param {Address} shippingAddress - The shipping address to set.
 * @param {Address} billingAddress - The billing address to set.
 * @param {PaymentIntentResult} [stripeData] - Optional Stripe payment intent data.
 * 
 * @returns {Promise<{ success: boolean; message: string }>} - The result of the address update.
 */

// 3rd Party Imports
import { Address } from '@/lib/types/address';
import {PaymentIntentResult} from "@stripe/stripe-js";

// Function Imports
import { addAuthorizationHeader } from '@/lib/functions/addAuthorizationHeader';
import { getSession } from 'next-auth/react'; 

// Type Imports
import type { OrderState } from '@/lib/types/orderState';

import { formatProvinceCode } from '@/lib/functions/formatProvinceCode';
import { resolvePartyAllowSubstitutionsForCartUpdate } from '@/lib/functions/ensurePartyOrderSubstitutions';

const normalizeAddress = (address: Address): Address => {
  const countryCode = (address.countryCode ?? 'US').toUpperCase();

  return {
    ...address,
    firstName: address.firstName?.trim() || 'Guest',
    lastName: address.lastName?.trim() || 'Customer',
    phoneNumber: address.phoneNumber?.trim() || '5555555555',
    provinceCode: formatProvinceCode(countryCode, address.provinceCode ?? ''),
    countryCode,
  };
};

export async function setOrderAddress(
  orderState: OrderState,
  email: string,
  shippingAddress: Address,
  billingAddress: Address,
  stripeData?: PaymentIntentResult,
  allowSubstitutions?: boolean,
  couponCode?: string,
) {
  if (!orderState?.token) {
    return { success: false, message: 'Order token is required' };
  }

  try {
    const session = await getSession();

    const headers = addAuthorizationHeader(
      { 'Content-Type': 'application/json' },
      orderState.tokenType == 'customer' ? session?.token : null,
    );

    const requestBody: {
      email: string;
      billingAddress: Address;
      shippingAddress: Address;
      allowSubstitutions?: boolean;
      couponCode?: string;
      stripeData?: string;
      stripeId?: string;
    } = {
      email,
      billingAddress: normalizeAddress(billingAddress),
      shippingAddress: normalizeAddress(shippingAddress),
    };

    if (couponCode) {
      requestBody.couponCode = couponCode;
    }

    // Always send allowSubstitutions so UpdateCart does not default substitutions back on.
    requestBody.allowSubstitutions = resolvePartyAllowSubstitutionsForCartUpdate(
      orderState.token,
      allowSubstitutions,
    );

    if (stripeData) {
      requestBody.stripeData = JSON.stringify(stripeData.paymentIntent);
      requestBody.stripeId = stripeData.paymentIntent?.id;
    }

    const resp = await fetch(`/api/orders/${orderState.token}/`, {
      method: 'PUT',
      body: JSON.stringify(requestBody),
      headers,
      next: {
        revalidate: 0,
      },
    });

    if (resp.status === 200) {
      return { success: true, message: '' };
    }

    let message = 'We could not update your address. Please check your information and try again.';
    try {
      const data = await resp.json();
      if (typeof data?.error === 'string' && data.error.trim()) {
        message = data.error;
      } else if (typeof data?.message === 'string' && data.message.trim()) {
        message = data.message;
      } else if (typeof data?.['hydra:description'] === 'string' && data['hydra:description'].trim()) {
        message = data['hydra:description'];
      }
    } catch {
      // use default message
    }

    return { success: false, message };
  } catch {
    return { success: false, message: 'We could not update your address. Please try again.' };
  }
}
